import { useMemo } from 'react';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { ScenarioConstants } from 'app/modules/rules/models';
import { WidgetProps } from 'app/modules/detectionModels/components/scenarioWidgets/models';
import {
  U21Alert,
  U21LoadError,
  U21Select,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { selectHasReadMatchlistsPermission } from 'app/modules/session/selectors';
import { useGetMatchlists } from 'app/modules/matchlist/queries/useGetMatchlists';
import { getMatchlistTypes } from 'app/modules/detectionModels/helpers';
import styled from 'styled-components';

export const ScenarioBlacklistSelectorNew = ({
  viewOnly,
  editingScenario,
  onChange,
}: WidgetProps) => {
  const hasReadMatchlistsPermission = useSelector(
    selectHasReadMatchlistsPermission,
  );

  const { data, isLoading, isError, refetch } = useGetMatchlists({
    offset: 1,
    limit: 50,
    types: getMatchlistTypes(editingScenario.name),
  });

  const options = useMemo(() => {
    if (!data?.blacklists) return [];
    return data.blacklists
      .filter(({ status }) => status === 'ACTIVE')
      .map(({ id, name }) => ({ text: name, value: id }));
  }, [data]);

  const selectedId = editingScenario.parameters[ScenarioConstants.BLACKLIST_ID];

  if (!hasReadMatchlistsPermission) {
    return (
      <AlertContainer>
        <StyledU21Alert color="warning">
          You do not have permission to access matchlists.
        </StyledU21Alert>
      </AlertContainer>
    );
  }

  if (isError) {
    return <U21LoadError label="matchlists" onTryAgain={() => refetch()} />;
  }

  const handleChange = (value: number) => {
    const newEditingScenario = cloneDeep(editingScenario);
    onChange({
      ...newEditingScenario,
      parameters: {
        ...newEditingScenario.parameters,
        [ScenarioConstants.BLACKLIST_ID]: value,
      },
    });
  };

  return (
    <StyledU21Spacer horizontal>
      <U21Typography variant="body1" inline>
        Matchlist:{' '}
      </U21Typography>
      <U21Select
        disabled={viewOnly}
        clearable={false}
        loading={isLoading}
        onChange={handleChange}
        options={options}
        placeholder="Select a matchlist"
        responsiveLength
        value={selectedId}
      />
    </StyledU21Spacer>
  );
};

const AlertContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0;
`;

const StyledU21Spacer = styled(U21Spacer)`
  margin: 8px 0;
  justify-content: center;
`;

const StyledU21Alert = styled(U21Alert)`
  margin: 8px 0;
`;
